import { setActiveBulkView, setActiveTab, resetCampaignAndTemplateThunk, setCampaignSortOptions, setBulkCampaignDialogSearchTerm } from '@common-reducers/CampaignReducer';
import { DBCampaignThunks, campaignsSelectByQuery } from "@common-reducers/DBServiceThunks";
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { cleanMessageLogsFromStateExceptRunningCampaignsThunk } from '@common/reducers/CampaignThunks';
import { selectBulkCampaignDialogSearchTerm, selectRunningNotCompletedCampaigns } from '@common-reducers/CampaignSelectors';
import { ProductEvent, trackProductEvent } from '@common-services/product-analytics';
import { Plus } from 'lucide-react';
import { useEffect } from 'react';
import BulkButton from './bulk-button';
import BulkSearchField from './bulk-search-field';
import CampaignDialogRow from './campaign-dialog-row';
import LazyLoadingVirtuoso from './lazy-loading-virtuoso';
import CampaignSortButton from './campaign-sort-button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

export default function BulkCampaignDialog() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(namespace);
    const campaignSortOptions = useAppSelector((state) => state.CampaignReducer.campaignSortOptions);
    const searchTerm = useAppSelector(selectBulkCampaignDialogSearchTerm);

    const handleNewCampaignClick = () => {
        // Reset campaign and template state before starting new campaign
        dispatch(resetCampaignAndTemplateThunk());
        dispatch(setActiveTab('audiences'));
        dispatch(setActiveBulkView("newCampaignView"))
        trackProductEvent(ProductEvent.CreateCampaign, {
            category: "campaign",
            action: "create_campaign_initiated",
            feature_location: "bulk_dashboard"
        });
    };

    const handleSearchChange = (value: string) => {
        dispatch(setBulkCampaignDialogSearchTerm(value));
    };

    const cleanDBMessageLogState = async () => {
        dispatch(cleanMessageLogsFromStateExceptRunningCampaignsThunk())
    }

    const handleSortChange = (sortOptions: any) => {
        dispatch(setCampaignSortOptions(sortOptions));
    }

    useEffect(() => {
        cleanDBMessageLogState()
        // Fetch running campaigns to ensure they are available for the pinned section
        dispatch(DBCampaignThunks.find({ campaignStatus: 'running' }));
    }, [])

    // Progress-aware: excludes 'running' campaigns whose messages are all processed (all sent), so a
    // finished campaign leaves "Running Now" immediately instead of lingering until the backend flips
    // its status. Same completion rule as the card's Completed badge.
    const runningCampaigns = useAppSelector(selectRunningNotCompletedCampaigns);

    const commonFilterObj = {
        ...(searchTerm ? {
            $or: [
                { 'template.name': { $regex: searchTerm, $options: 'i' } },
                { 'template.message': { $regex: searchTerm, $options: 'i' } },
                { 'template.poll.question': { $regex: searchTerm, $options: 'i' } },
                { name: { $regex: searchTerm, $options: 'i' } },
            ],
        } : {}),
    };

    return (
        <div className="flex flex-col w-full h-full">
            {/* Search and Actions Bar */}
            <div className="flex items-center gap-4 mb-6 mt-8">
                <div className="flex-1 relative">
                    <BulkSearchField
                        searchTerm={searchTerm}
                        setSearchTerm={handleSearchChange}
                        placeHolderString={t('campaign.searchPlaceholder')}
                        className="w-full"
                    />
                </div>
                <CampaignSortButton
                    sortOptions={campaignSortOptions}
                    onSortChange={handleSortChange}
                />
                <BulkButton
                    onClick={handleNewCampaignClick}
                    buttonString={t('campaign.newCampaign')}
                    Icon={Plus}
                    buttonWidth='auto'
                />
            </div>

            <div className="flex-1 min-h-0 w-full pb-6">
                {runningCampaigns.length > 0 ? (
                    <Tabs defaultValue="running" className="w-full h-full flex flex-col">
                        <TabsList className="grid w-full grid-cols-2 mb-4">
                            <TabsTrigger value="running">{t('campaign.runningNow')}</TabsTrigger>
                            <TabsTrigger value="all">{t('campaign.allCampaigns')}</TabsTrigger>
                        </TabsList>
                        <TabsContent value="running" className="mt-0 flex-1 min-h-0">
                            <LazyLoadingVirtuoso
                                isFullWidth={true}
                                height="100%"
                                rowHeightString='auto'
                                filterObj={{
                                    campaignStatus: 'running',
                                    ...commonFilterObj
                                }}
                                DBThunks={DBCampaignThunks}
                                DBReducer={state => state.DBCampaignReducer}
                                selectByQuery={campaignsSelectByQuery}
                                // Progress-aware list: 'running' campaigns that aren't all-processed yet.
                                // Keeps completed campaigns out of the tab (see selectRunningNotCompletedCampaigns).
                                selector={selectRunningNotCompletedCampaigns}
                                Component={CampaignDialogRow}
                                findObj={{
                                    getAlsoCountContact: true,
                                }}
                                sortOptions={campaignSortOptions}
                            />
                        </TabsContent>
                        <TabsContent value="all" className="mt-0 flex-1 min-h-0">
                            <LazyLoadingVirtuoso
                                isFullWidth={true}
                                height="100%"
                                rowHeightString='auto'
                                filterObj={{
                                    ...(campaignSortOptions.statusFilter && { campaignStatus: campaignSortOptions.statusFilter }),
                                    ...commonFilterObj
                                }}
                                DBThunks={DBCampaignThunks}
                                DBReducer={state => state.DBCampaignReducer}
                                selectByQuery={campaignsSelectByQuery}
                                Component={CampaignDialogRow}
                                findObj={{
                                    getAlsoCountContact: true,
                                }}
                                sortOptions={campaignSortOptions}
                            />
                        </TabsContent>
                    </Tabs>
                ) : (
                    <LazyLoadingVirtuoso
                        isFullWidth={true}
                        height="100%"
                        rowHeightString='auto'
                        filterObj={{
                            ...(campaignSortOptions.statusFilter && { campaignStatus: campaignSortOptions.statusFilter }),
                            ...commonFilterObj
                        }}
                        DBThunks={DBCampaignThunks}
                        DBReducer={state => state.DBCampaignReducer}
                        selectByQuery={campaignsSelectByQuery}
                        Component={CampaignDialogRow}
                        findObj={{
                            getAlsoCountContact: true,
                        }}
                        sortOptions={campaignSortOptions}
                    />
                )}
            </div>
        </div>
    )
}
